import React, { useEffect, useState } from "react";
import "./BuyerSellerStock.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";

export default function BuyerSellerStock() {
    const { buyerSellerStockData } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl,
        ["buyerSellerStockData"]
    );

    const tableColumns = [
        {
            key: "symbol",
            label: "SYMBOL",
            render: (row) => row.symbol ?? "--",
        },
        {
            key: "prevClose",
            label: "PREV CLOSE",
            render: (row) => (row.prevClose != null ? row.prevClose : "--"),
        },
        {
            key: "open",
            label: "OPEN",
            render: (row) => (row.open != null ? row.open : "--"),
        },
        {
            key: "currentPrice",
            label: "LTP",
            render: (row) => (row.currentPrice != null ? row.currentPrice : "--"),
        },
        {
            key: "high",
            label: "HIGH",
            render: (row) => (row.high != null ? row.high : "--"),
        },
        {
            key: "low",
            label: "LOW",
            render: (row) => (row.low != null ? row.low : "--"),
        },
        {
            key: "currPerChange",
            label: "CURR CHG(%)",
            render: (row) => (row.currPerChange != null ? row.currPerChange : "--"),
        },
        
        {
            key: "perChange",
            label: "CHNG(%)",
            render: (row) => (row.perChange != null ? `${row.perChange}` : "--"),
        },

        {
            key: "priceChange",
            label: "PRICE CHANGE",
            render: (row) => (row.priceChange != null ? row.priceChange : "--"),
        },
        {
            key: "dttime",
            label: "DATE & TIME",
            render: (row) => (row.dttime != null ? row.dttime : "--"),
        }
    ];

    const [buySellStockData, setBuySellStockData] = useState([]);

    const [get52WKHighData, set52WKHighData] = useState([]);
    const [get52WKLowData, set52WKLowData] = useState([]);
    const [mostActiveStocksByValueData, setMostActiveStocksByValueData] = useState([]);
    const [onlyBuyersData, setOnlyBuyersData] = useState([]);
    const [onlySellersData, setOnlySellersData] = useState([]);
    const [priceShokersData, setPriceShokersData] = useState([]);
    const [topGainersData, setTopGainersData] = useState([]);
    const [topLoosersData, setTopLoosersData] = useState([]);
    const [volumeShockersData, setVolumeShockersData] = useState([]);

    const [activeTab, setActiveTab] = useState("TOP_GAINERS");

    useEffect(() => {
        if (buyerSellerStockData)
            set52WKHighData(buyerSellerStockData?.categories?.["52weekhigh"] || []);
        set52WKLowData(buyerSellerStockData?.categories?.["52weeklow"] || []);
        setMostActiveStocksByValueData(buyerSellerStockData?.categories?.mostactivestocks
            || []);
        setOnlyBuyersData(buyerSellerStockData?.categories?.onlybuyers || []);
        setOnlySellersData(buyerSellerStockData?.categories?.onlysellers || []);
        setPriceShokersData(buyerSellerStockData?.categories?.priceshockers || []);
        setTopGainersData(buyerSellerStockData?.categories?.topgainers || []);
        setTopLoosersData(buyerSellerStockData?.categories?.toplosers || []);
        setVolumeShockersData(buyerSellerStockData?.categories?.volumeshockers || []);
        console.log("Received buyerSellerStockData update: ", buyerSellerStockData);
        console.log("Received categories update: ", buyerSellerStockData?.categories);
        if (Array.isArray(buyerSellerStockData?.list) && buyerSellerStockData?.list?.length > 0) {
            setBuySellStockData(buyerSellerStockData?.list || []);
            console.log("buyerSellerStockData : ", buyerSellerStockData?.list);
        }

    }, [buyerSellerStockData]);

    const getRows = (dataset) => (Array.isArray(dataset?.rows) ? dataset.rows : []);

    const normalizeSymbol = (symbol) =>
        String(symbol || "").trim().toUpperCase();

    const getSignedCellClass = (columnKey, value) => {
        if (columnKey !== "currPerChange" && columnKey !== "perChange") {
            return "";
        }

        const numericValue = Number.parseFloat(String(value).replace("%", ""));

        if (Number.isNaN(numericValue) || numericValue === 0) {
            return "";
        }

        return numericValue > 0 ? "positive-cell" : "negative-cell";
    };

    const getCommonSymbols = (sourceRows, minMatchCount = sourceRows.length) => {
        const isReady = sourceRows.every((rows) => rows.length > 0);

        if (!isReady) {
            return { isReady, symbols: new Set() };
        }

        const symbolCounts = new Map();

        sourceRows.forEach((rows) => {
            const uniqueSymbols = new Set(
                rows
                    .map((row) => normalizeSymbol(row.symbol))
                    .filter(Boolean)
            );

            uniqueSymbols.forEach((symbol) => {
                symbolCounts.set(symbol, (symbolCounts.get(symbol) || 0) + 1);
            });
        });

        return {
            isReady,
            symbols: new Set(
                Array.from(symbolCounts.entries())
                    .filter(([, count]) => count >= minMatchCount)
                    .map(([symbol]) => symbol)
            ),
        };
    };

    const buildRowsForSymbols = (sourceRows, matchedSymbols) => {
        const rowMap = new Map();

        sourceRows.forEach((rows) => {
            rows.forEach((row) => {
                const symbol = normalizeSymbol(row.symbol);

                if (symbol && matchedSymbols.has(symbol) && !rowMap.has(symbol)) {
                    rowMap.set(symbol, row);
                }
            });
        });

        return Array.from(rowMap.values());
    };

    const buildSymbolSet = (rows) =>
        new Set(
            rows
                .map((row) => normalizeSymbol(row.symbol))
                .filter(Boolean)
        );

    const commonBuyStockSourceRows = [
        getRows(topGainersData),
        getRows(get52WKHighData),
        getRows(onlyBuyersData),
        getRows(volumeShockersData),
        getRows(mostActiveStocksByValueData),
    ];

    const commonSellStockSourceRows = [
        getRows(topLoosersData),
        getRows(get52WKLowData),
        getRows(onlySellersData),
        getRows(volumeShockersData),
        getRows(mostActiveStocksByValueData),
    ];

    const {
        isReady: isBuyStockSourcesReady,
        symbols: buyStockSymbols,
    } = getCommonSymbols(commonBuyStockSourceRows, 4);

    const {
        isReady: isSellStockSourcesReady,
        symbols: sellStockSymbols,
    } = getCommonSymbols(commonSellStockSourceRows, 4);

    const buyStock = buildRowsForSymbols(commonBuyStockSourceRows, buyStockSymbols);

    const sellStock = buildRowsForSymbols(commonSellStockSourceRows, sellStockSymbols);

    const stockCategorySets = [
        { code: "TG", symbols: buildSymbolSet(getRows(topGainersData)) },
        { code: "TL", symbols: buildSymbolSet(getRows(topLoosersData)) },
        { code: "52WH", symbols: buildSymbolSet(getRows(get52WKHighData)) },
        { code: "52WL", symbols: buildSymbolSet(getRows(get52WKLowData)) },
        { code: "BUY", symbols: buildSymbolSet(getRows(onlyBuyersData)) },
        { code: "SELL", symbols: buildSymbolSet(getRows(onlySellersData)) },
        { code: "PS", symbols: buildSymbolSet(getRows(priceShokersData)) },
        { code: "VS", symbols: buildSymbolSet(getRows(volumeShockersData)) },
        { code: "MAS", symbols: buildSymbolSet(getRows(mostActiveStocksByValueData)) },
    ];

    const addStockTagsToRows = (rows) => rows.map((row) => {
        const symbol = normalizeSymbol(row.symbol);
        const stockTags = stockCategorySets
            .filter((category) => category.symbols.has(symbol))
            .map((category) => category.code);

        return {
            ...row,
            stockTags,
        };
    });

    const taggedTableColumns = [
        tableColumns[0],
        {
            key: "stockTags",
            label: "TAGS",
            headerClassName: "tags-column",
            cellClassName: "tags-cell",
            render: (row) => (
                row.stockTags?.length > 0 ? (
                    <div className="stock-tags">
                        {row.stockTags.map((tag) => (
                            <span key={`${row.symbol}-${tag}`} className="stock-tag-badge">
                                {tag}
                            </span>
                        ))}
                    </div>
                ) : "--"
            ),
        },
        ...tableColumns.slice(1),
    ];

    const topGainerRows = addStockTagsToRows(getRows(topGainersData));
    const topLooserRows = addStockTagsToRows(getRows(topLoosersData));
    const high52WKRows = addStockTagsToRows(getRows(get52WKHighData));
    const low52WKRows = addStockTagsToRows(getRows(get52WKLowData));
    const onlyBuyerRows = addStockTagsToRows(getRows(onlyBuyersData));
    const onlySellerRows = addStockTagsToRows(getRows(onlySellersData));
    const priceShokerRows = addStockTagsToRows(getRows(priceShokersData));
    const volumeShokerRows = addStockTagsToRows(getRows(volumeShockersData));
    const mostActiveByValueRows = addStockTagsToRows(getRows(mostActiveStocksByValueData));

    const renderRows = (rows, emptyMessage = "Waiting for live data...", columns = tableColumns) => (
        rows?.length > 0 ? (
            rows.map((row, idx) => (
                <tr key={idx}>
                    {columns.map((column) => (
                        <td
                            key={column.key}
                            data-label={column.label}
                            className={[
                                column.cellClassName || "",
                                getSignedCellClass(column.key, row[column.key]),
                            ].filter(Boolean).join(" ")}
                        >
                            {column.render(row)}
                        </td>
                    ))}
                </tr>
            ))
        ) : (
            <tr className="empty-state-row">
                <td colSpan={columns.length}>{emptyMessage}</td>
            </tr>
        )
    );

    const renderTable = (rows, emptyMessage, columns = tableColumns) => (
        <div className="table-container">
            <div className="table-scroll-area">
                <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table">
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    scope="col"
                                    className={column.headerClassName || ""}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {renderRows(rows, emptyMessage, columns)}
                    </tbody>
                </table>
            </div>

            <div className="fixed-footer">
                <span>noptionwave © 2026</span>
            </div>
        </div>
    );

    const rendercommonBuyStocksTable = () =>
        renderTable(
            buyStock,
            isBuyStockSourcesReady
                ? "No buyStock found with matches in at least 4 of Top Gainers, 52 WK High, Only Buyers, Volume Shockers, and Most Active By Value."
                : "Waiting for all buy stock categories..."
        );

    const renderCommonSellStocksTable = () =>
        renderTable(
            sellStock,
            isSellStockSourcesReady
                ? "No sellStock found with matches in at least 4 of Top Losers, 52 WK Low, Only Sellers, Volume Shockers, and Most Active By Value."
                : "Waiting for all sell stock categories..."
    );

    const renderTableForTopGainers = () => (
        renderTable(topGainerRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableForTopLoosers = () => (
        renderTable(topLooserRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableFor52WKHigh = () => (
        renderTable(high52WKRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableFor52WKLow = () => (
        renderTable(low52WKRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableForOnlyBuyers = () => (
        renderTable(onlyBuyerRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableForOnlySellers = () => (
        renderTable(onlySellerRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableForPriceShokers = () => (
        renderTable(priceShokerRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableForVolumeShokers = () => (
        renderTable(volumeShokerRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderTableForMostActiveByValue = () => (
        renderTable(mostActiveByValueRows, "Waiting for live data...", taggedTableColumns)
    );

    const renderPlaceholder = (title, description) => (
        <div className="table-container">
            <div className="tab-placeholder">
                <h2>{title}</h2>
                <p>{description}</p>
            </div>
        </div>
    );

    const renderActiveTabContent = () => {
        if (activeTab === "BUY") {
            return rendercommonBuyStocksTable();
        }
        if (activeTab === "SELL") {
            return renderCommonSellStocksTable();
        }
        if (activeTab === "TOP_GAINERS") return renderTableForTopGainers();
        if (activeTab === "TOP_LOOSERS") {
            return renderTableForTopLoosers();
            // return renderPlaceholder(
            //     "BEST STOCK",
            //     "This tab is switching correctly. Connect the Buyer Seller Stock data source for BEST STOCK content if you want separate data here."
            // );
        }
        if (activeTab === "52_WK_HIGH") {
            return renderTableFor52WKHigh()
        }
        if (activeTab === "52_WK_LOW") {
            return renderTableFor52WKLow()
        }
        if (activeTab === "ONLY_BUYERS") {
            return renderTableForOnlyBuyers()
        }

        if (activeTab === "ONLY_SELLERS") {
            return renderTableForOnlySellers();
        }

        if (activeTab === "PRICE_SHOCKERS") {
            return renderTableForPriceShokers();
        }

        if (activeTab === "VOLUME_SHOCKERS") {
            return renderTableForVolumeShokers();
        }

        if (activeTab === "MOST_ACTIVE_BY_VALUE") {
            return renderTableForMostActiveByValue();
        }
        return renderTableForTopGainers();
    };

    return (
        <div className="buyer-seller-stock">
            <div className="top-fixed-shell">
                <header className="header">
                    <nav className="nav-container">
                        <div className="header-flex">
                            <div className="left-nav">
                                <div className="logo-container">
                                    <img
                                        src="/assets/images/noptionwave-logo-1.png"
                                        className="header-logo"
                                        alt="logo"
                                    />
                                </div>
                            </div>

                            {/* <div className="right-nav">
                            <span className="header-line">Date : {displayDate}</span>
                            <span className="header-line">Time : {displayTime}</span>
                        </div> */}
                        </div>
                    </nav>
                </header>

                <div className="tab-container">
                    <div className="tab">
                        {/* <button type="button" className={`tablinks sub-header-font ${activeTab === "BUY" ? "active" : ""}`}
                            onClick={() => setActiveTab("BUY")}  >
                            BUY
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "SELL" ? "active" : ""}`}
                            onClick={() => setActiveTab("SELL")}  >
                            SELL
                        </button> */}

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "TOP_GAINERS" ? "active" : ""}`}
                            onClick={() => setActiveTab("TOP_GAINERS")}  >
                            GAINERS
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "TOP_LOOSERS" ? "active" : ""}`}
                            onClick={() => setActiveTab("TOP_LOOSERS")}  >
                            LOSERS
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "52_WK_HIGH" ? "active" : ""}`}
                            onClick={() => setActiveTab("52_WK_HIGH")}  >
                            52 WK HIGH
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "52_WK_LOW" ? "active" : ""}`}
                            onClick={() => setActiveTab("52_WK_LOW")} >
                            52 WK LOW
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "ONLY_BUYERS" ? "active" : ""}`}
                            onClick={() => setActiveTab("ONLY_BUYERS")} >
                            BUYERS
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "ONLY_SELLERS" ? "active" : ""}`}
                            onClick={() => setActiveTab("ONLY_SELLERS")} >
                            SELLERS
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "PRICE_SHOCKERS" ? "active" : ""}`}
                            onClick={() => setActiveTab("PRICE_SHOCKERS")} >
                            PRICE SHOCKERS
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "VOLUME_SHOCKERS" ? "active" : ""}`}
                            onClick={() => setActiveTab("VOLUME_SHOCKERS")} >
                            VOL SHOCKERS
                        </button>

                        <button type="button" className={`tablinks sub-header-font ${activeTab === "MOST_ACTIVE_BY_VALUE" ? "active" : ""}`}
                            onClick={() => setActiveTab("MOST_ACTIVE_BY_VALUE")} >
                            MOST ACTIVE
                        </button>

                    </div>
                </div>
            </div>

            {renderActiveTabContent()}
        </div>
    );
}
